import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-4334">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4335">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-4336">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-4337">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-4338" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-4339">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4340" />
              <p className="text-sm text-gray-600" data-id="element-4341">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-4342">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-4343" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-4344">
          {/* Quality & Metadata */}
          <div data-id="element-4345">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4346">
              <Brain className="w-3.5 h-3.5" data-id="element-4347" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-4348">
              <div className="grid grid-cols-2 gap-4" data-id="element-4349">
                <div data-id="element-4350">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4351">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4352">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-4353">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4354">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-4355">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-4356">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-4357">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-4358">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4359">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4360">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-4361">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-4362">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-4363">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-4364">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4365">
              <Database className="w-3.5 h-3.5" data-id="element-4366" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-4367">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4368" />
                <span className="text-sm text-gray-600" data-id="element-4369">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-4370">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-4371">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-4372">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4373">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-4374" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-4375">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-4376">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4377" />
                  <span className="text-sm" data-id="element-4378">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-4379">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-4380" />
                  <span className="text-sm" data-id="element-4381">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-4382">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-4383">
              <BookOpen className="w-3.5 h-3.5" data-id="element-4384" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-4385">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-4386">
                      <div data-id="element-4387">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4388">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4389">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DOK_MISSING') ? <span className="text-amber-600" data-id="element-4390">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-4391">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-4392">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-4393">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-4394">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_MISCONCEPTION_MISSING') ? <span className="text-amber-500" data-id="element-4395">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-4396">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-4397">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-4398" />
                        <span className="text-sm text-gray-600" data-id="element-4399">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-4400">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-4401" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4402" />}
                        <span className="text-sm text-gray-600" data-id="element-4403">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}